import { Flex } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { dispatchAppEvent } from 'src/common/events/appEvents';
import PageLoader from 'src/UI/loader/PageLoader';
import { MeetingsEmptyView } from 'src/meetings/components/MeetingsEmptyView';
import { useCalendarEvents } from 'src/meetings/hooks/useCalendarEvents';
import { useCalendars } from 'src/meetings/hooks/useCalendars';
import { TAB_NAME, UpcomingEventsPanel } from 'src/meetings/home/UpcomingEventsPanel';
import { EventDetailPanel } from 'src/meetings/home/EventDetailPanel';

export const MeetingsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Steering deep-links to a specific meeting via ?eventId= (carrying any other
  // params, e.g. generate_agenda); send those on to the meeting detail screen.
  useEffect(() => {
    const eventId = searchParams.get('eventId');
    if (!eventId) {
      return;
    }
    const rest = new URLSearchParams(searchParams);
    rest.delete('eventId');
    const query = rest.toString();
    navigate(`/meetings/${eventId}${query ? `?${query}` : ''}`, { replace: true });
  }, []);

  const { justConnected, calendars, isLoading: calendarsLoading } = useCalendars();
  const [tabName, setTabName] = useState<(typeof TAB_NAME)[keyof typeof TAB_NAME]>(TAB_NAME.CLIENT);

  const { refetch: refetchUpcoming } = useCalendarEvents({
    pastOrUpcoming: 'UPCOMING',
    clientMeetingsOnly: tabName === TAB_NAME.CLIENT ? true : undefined,
  });
  const { refetch: refetchPast } = useCalendarEvents({
    pastOrUpcoming: 'PAST',
    clientMeetingsOnly: true,
  });
  const { events: allUpcomingEvents } = useCalendarEvents({
    pastOrUpcoming: 'UPCOMING',
  });
  const { events: allPastEvents } = useCalendarEvents({
    pastOrUpcoming: 'PAST',
  });

  useEffect(() => {
    if (!justConnected) {
      return;
    }
    refetchUpcoming();
    refetchPast();
  }, [justConnected]);

  useEffect(() => {
    refetchUpcoming();
    refetchPast();
  }, [calendars]);

  const allEventsCount = allUpcomingEvents.length + allPastEvents.length;
  const prevEventCountRef = useRef(allEventsCount);
  useEffect(() => {
    const prev = prevEventCountRef.current;
    prevEventCountRef.current = allEventsCount;
    if (prev === 0 && allEventsCount > 0) {
      dispatchAppEvent('LOADED_CALENDAR_EVENTS');
    }
  }, [allEventsCount]);

  if (calendarsLoading) {
    return <PageLoader />;
  }

  return (
    <>
      <MeetingsEmptyView calendars={calendars} isLoading={calendarsLoading} />

      {calendars.length > 0 && (
        <Flex h="100%">
          <Flex direction="column" flex={4} bg="gray.1" style={{ overflowY: 'scroll' }}>
            <UpcomingEventsPanel
              tabName={tabName}
              onTabChange={setTabName}
              selectedEventId={null}
              onEventSelect={eventId => {
                if (eventId) {
                  navigate(`/meetings/${eventId}`);
                }
              }}
              onRefetchNeeded={refetchUpcoming}
              tabNameConstants={TAB_NAME}
            />
          </Flex>
          <Flex direction="column" flex={6} bg="white" style={{ overflowY: 'scroll' }}>
            <EventDetailPanel selectedEvent={undefined} onCloseEvent={() => {}} />
          </Flex>
        </Flex>
      )}
    </>
  );
};
